import { InputNumber, Input, Select, Typography, Form, Button, Popconfirm, Tooltip, Card, Space, Slider, Row, Col, Alert } from "antd"
import { DeleteOutlined, PlusOutlined, InfoCircleOutlined, SettingOutlined, SafetyOutlined, FilterOutlined } from "@ant-design/icons"
import { observer } from "mobx-react"
import GeoPickerElement from "components/geoPicker"
import { validateAppVersionFormat } from "../validators/appVersionFormatValidator"
import { validateEmailList } from "../validators/emailListValidator"
import { PatternTester, MatchType } from "../PatternTester"

const { Title, Text } = Typography
const { Option } = Select

const formatPercent = (value?: number | string) => {
  const numeric = typeof value === "number" ? value : parseFloat(value || "0")
  if (Number.isNaN(numeric)) return ""

  return `${(numeric * 100).toFixed(2)}%`
}

const parsePercent = (value?: string) => {
  const cleaned = (value ?? "").replace(/%/g, "")
  const numeric = parseFloat(cleaned)
  if (Number.isNaN(numeric)) return 0

  return numeric / 100
}

type Props = { configType: "monitoringConfig" | "iframeMonitoringConfig" };

export const MonitoringFields = observer(({ configType }: Props) => (
  <div style={{ maxWidth: 1200 }}>
    <Title level={2} style={{ marginBottom: 24 }}>
      {configType === "monitoringConfig" ? "Desktop App Monitoring Config" : "Iframe Monitoring Config"}
    </Title>

    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Basic Configuration Card */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SettingOutlined style={{ color: "#1890ff" }} />
            <Text strong>Basic Configuration</Text>
          </div>
        }
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Form.Item
            name="monitoringSampleRate"
            label={
              <Space direction="vertical" size={2}>
                <Text strong>Monitoring Sample Rate</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Percentage of users who will have monitoring enabled
                </Text>
              </Space>
            }
            rules={[{ required: true, message: "Sample rate is required" }]}
            style={{ marginBottom: 0 }}
          >
            <Form.Item noStyle shouldUpdate>
              {form => {
                const value = form.getFieldValue("monitoringSampleRate") || 0

                return (
                  <div>
                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                      <Col flex="auto">
                        <Slider
                          min={0}
                          max={1}
                          step={0.0001}
                          value={value}
                          onChange={val => form.setFieldsValue({ monitoringSampleRate: val })}
                          tooltip={{ formatter: val => formatPercent(val) }}
                        />
                      </Col>
                      <Col>
                        <InputNumber
                          min={0}
                          max={1}
                          step={0.0001}
                          precision={4}
                          value={value}
                          onChange={val => form.setFieldsValue({ monitoringSampleRate: val })}
                          style={{ width: 80 }}
                          formatter={formatPercent}
                          parser={parsePercent}
                        />
                      </Col>
                    </Row>

                    {value === 0 && (
                      <Alert
                        message="No monitoring data will be collected"
                        type="warning"
                        showIcon
                      />
                    )}

                    {value === 1 && (
                      <Alert
                        message="All users will have monitoring enabled"
                        type="success"
                        showIcon
                      />
                    )}
                  </div>
                )
              }}
            </Form.Item>
          </Form.Item>

          <Form.Item
            name="minimumSupportedVersion"
            label={
              <Space direction="vertical" size={2}>
                <Text strong>
                  Minimum Supported Version{" "}
                  <Tooltip title="Sentry monitoring will be enabled only for users with this version or newer">
                    <InfoCircleOutlined style={{ color: "#1890ff" }} />
                  </Tooltip>
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Users with older versions will not have monitoring enabled
                </Text>
              </Space>
            }
            rules={[{ validator: validateAppVersionFormat }]}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder="Enter minimum version (e.g., 0.9.15 or 0.9.15.0)"
            />
          </Form.Item>
        </Space>
      </Card>

      {/* Access Control Card */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SafetyOutlined style={{ color: "#52c41a" }} />
            <Text strong>Access Control</Text>
          </div>
        }
      >
        <Form.Item
          name="emailWhitelist"
          label={<Text strong>Whitelisted Emails (100% Sample Rate)</Text>}
          rules={[{ validator: validateEmailList }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            mode="tags"
            tokenSeparators={[",", " ", "\n", "\t", ";"]}
            placeholder="Enter email addresses"
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          style={{ marginBottom: 0, marginTop: 24 }}
          label={
            <Space direction="vertical" size={2}>
              <Text strong>Country Blacklist</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Sentry events from users in these countries will be discarded. Checks both account country and IP country.
              </Text>
            </Space>
          }
        >
          <GeoPickerElement
            name="countryBlacklist"
            label=""
          />
        </Form.Item>
      </Card>

      {/* Event Filtering Card */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FilterOutlined style={{ color: "#fa8c16" }} />
            <Text strong>Event Filtering</Text>
          </div>
        }
        style={{ marginBottom: 24 }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Text strong>Monitoring Event Blacklist</Text>

          <Form.List name="blacklist">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {fields.map(field => (
                  <div
                    key={`${configType}-${field.key}`}
                    style={{
                      display: "flex",
                      gap: 12,
                      padding: 16,
                      backgroundColor: "#fafafa",
                      borderRadius: 6,
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <Form.Item
                      {...field}
                      label="Pattern"
                      name={[field.name, "pattern"]}
                      rules={[{ required: true, message: "Please input pattern!" }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Enter pattern" />
                    </Form.Item>

                    <Form.Item
                      {...field}
                      label="Match Type"
                      name={[field.name, "matchType"]}
                      rules={[{ required: true, message: "Please select match type!" }]}
                      style={{ width: 140, marginBottom: 0 }}
                    >
                      <Select>
                        <Option value={MatchType.EXACT}>Exact</Option>
                        <Option value={MatchType.PARTIAL}>Partial</Option>
                        <Option value={MatchType.REGEX}>Regex</Option>
                      </Select>
                    </Form.Item>

                    <div style={{ display: "flex", alignItems: "end", paddingBottom: 8 }}>
                      <Popconfirm
                        title="Remove this item?"
                        onConfirm={() => remove(field.name)}
                        okText="Remove"
                        placement="left"
                        okType="danger"
                      >
                        <Button type="text" icon={<DeleteOutlined />} danger />
                      </Popconfirm>
                    </div>
                  </div>
                ))}

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => add({ pattern: "", matchType: MatchType.EXACT })}
                    style={{ height: 40 }}
                  >
                    Add Blacklist Item
                  </Button>
                </Form.Item>
              </Space>
            )}
          </Form.List>

          {/* Pattern Tester Integration */}
          <Form.Item noStyle shouldUpdate>
            {form => {
              const blacklistPatterns = form.getFieldValue("blacklist") || []
              const validPatterns = blacklistPatterns
                .filter((item: { pattern?: string; matchType?: MatchType }) => item?.pattern && item?.matchType)
                .map((item: { pattern: string; matchType: MatchType }) => ({
                  pattern: item.pattern,
                  matchType: item.matchType,
                }))

              return <PatternTester patterns={validPatterns} />
            }}
          </Form.Item>
        </Space>
      </Card>
    </Space>
  </div>
))
